import React, { useEffect } from 'react';
import { motion } from 'framer-motion';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface NotificationProps {
  type: NotificationType;
  isOpen: boolean;
  onClose: () => void;
  duration?: number;
  isConfirmation?: boolean; // Nouveau pour distinguer la confirmation
}

export const ModernNotification: React.FC<NotificationProps> = ({
  type,
  isOpen,
  onClose,
  duration = 5000,
  isConfirmation = false,
}) => {
  useEffect(() => {
    if (!isOpen || duration <= 0) return;

    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [isOpen, duration, onClose]);

  // Déterminer la couleur en fonction du type
  const getColor = () => {
    if (isConfirmation) {
      return 'bg-gradient-to-r from-lime-600 to-orange-500';
    }
    switch (type) {
      case 'success':
        return 'bg-green-600';
      case 'info':
        return 'bg-red-600';
      default:
        return 'bg-red-600'; // Par défaut pour error, warning
    }
  };

  // Animation avec framer-motion
  const containerVariants = {
    hidden: { y: '-100%' },
    visible: { y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
    exit: { y: '-100%', transition: { duration: 0.2 } },
  };

  const dotVariants = isConfirmation
    ? {
        hidden: { x: '-100%', opacity: 0.6, scale: 0.75 },
        visible: {
          x: '100%',
          opacity: [0.6, 1, 0.6],
          scale: [0.75, 1.1, 0.75],
          transition: {
            x: { duration: 4, ease: 'linear' },
            opacity: { duration: 1, repeat: Infinity, ease: 'easeInOut', delay: 4 },
            scale: { duration: 1, repeat: Infinity, ease: 'easeInOut', delay: 4 },
          },
        },
      }
    : {
        hidden: { x: '-100%', opacity: 0.6, scale: 0.75 },
        visible: {
          x: ['-100%', '100%', '-100%'],
          opacity: [0.6, 1, 0.6],
          scale: [0.75, 1.1, 0.75],
          transition: {
            x: { duration: 2, repeat: Infinity, ease: 'linear' },
            opacity: { duration: 1, repeat: Infinity, ease: 'easeInOut' },
            scale: { duration: 1, repeat: Infinity, ease: 'easeInOut' },
          },
        },
      };

  if (!isOpen) return null;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className={`fixed top-0 left-0 right-0 z-50 w-full h-[5px] ${getColor()}`}
      role="alert"
      aria-live="assertive"
    >
      <motion.div
        variants={dotVariants}
        initial="hidden"
        animate="visible"
        className="h-full w-[50px] bg-white rounded-full"
        style={{ transformOrigin: 'center' }}
      />
    </motion.div>
  );
};

export default ModernNotification;